<template>
  <div class="pb-24">
    <div class="p-6 bg-orange-light/40">
      <div class="flex items-center mb-1">
        <TheBackButton class="!p-0" />
        <h1 class="text-2xl font-bold">
          Rapprochement des patients
        </h1>
      </div>
      <p>Patients homonymes que vous avez rapprochés, comme étant identiques ou comme étant différents.</p>
    </div>
    <div v-if="isLoading" class="flex h-full justify-center items-center">
      <BaseFlexSpinner size="medium-small" />
    </div>
    <BaseCard v-else class="min-h-[400px]">
      <BaseCardHeader>
        <div class="flex items-center">
          <div class="flex items-center gap-2 border-r pr-4 mr-4">
            <CopyCheck class="text-primary" />
            {{ data?.data.length || 0 }} {{ pluralize('rapprochement', data?.data.length || 0) }}
          </div>
          <div class="border-r pr-4">
            <CustomerSearch
              class="w-full max-w-80 bg-gray-50"
              :callback="searchConsumer"
              placeholder="Rechercher un patient ..."
              @clear="parameters.consumer_key = undefined"
            />
          </div>
        </div>
      </BaseCardHeader>
      <div v-if="isValidating" class="flex h-full justify-center items-center">
        <BaseFlexSpinner size="medium-small" />
      </div>
      <div v-else class="flex flex-col px-6">
        <div v-for="(reconciliation, index) in data?.data" :key="index">
          <AnimatePresence>
            <motion.div
              v-if="!hiddenCandidateIds.includes(reconciliation.id)"
              :key="reconciliation.id"
              :initial="{ opacity: 1, x: 0 }"
              :exit="{ opacity: 0, x: -200 }"
              :transition="{ duration: 0.25, type: 'tween' }"
            >
              <ReconciliationRow :reconciliation="reconciliation" @hide="hiddenCandidateIds.push(reconciliation.id)" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </BaseCard>
  </div>
</template>

<script setup lang="ts">
import useSwr from "@/core/composables/swr/useSwr";
import {computed, reactive, ref} from "vue";
import {cacheKey} from "@/lib/cache";
import {reconciliationApi} from "@/container";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardHeader from "@/core/components/base/BaseCardHeader.vue";
import {CopyCheck} from "@lucide/vue";
import {motion, AnimatePresence} from "motion-v";
import TheBackButton from "@/core/components/navigation-bar/TheBackButton.vue";
import ReconciliationRow from "@/consumer-reconciliation/components/ReconciliationRow.vue";
import {pluralize} from "@/core/filters/Pluralize";
import CustomerSearch from "@/customer/components/customer-search/CustomerSearch.vue";

const hiddenCandidateIds = ref<number[]>([]);

const parameters = reactive<{
    consumer_key?: string;
}>({
    consumer_key: undefined,
});

/**
 * Search for a consumer key.
 *
 * @param type  The consumer type : beneficiary or customer.
 * @param _id
 * @param key The consumer key.
 */
const searchConsumer = (type: 'beneficiary' | 'customer' | null, _id?: string, key?: string) => {
    parameters.consumer_key = type && key ? key : undefined;
};

/**
 * Fetches reconciliation candidates.
 */
const {data, isLoading, isValidating} = useSwr(
    computed(() => cacheKey(`consumer-reconciliations`, parameters)),
    () => reconciliationApi().index(parameters),
);
</script>
